<template>
  <div class="container">
    <section v-show="!showSuccess">
      <div v-if="amountEditable ">
        <div class="center grey-text amount-label" v-if="showCashLabel">
          Cash/Check price:
        </div>
        <h3 class="bold-font center">Payment {{amount | currency}}</h3>
        <div class="row">
          <div class="col s12 m4 input-field">
            <input 
              @input="updateAmounts"
              name="amount"
              id="amount"
              v-model='amount'
              step="0.01"
              type="number" 
              inputmode="decimal"
              class='inputText'
              pattern="\d*"
              lang="en-001">

              <label for="amount">Amount</label>
            </div>
          </div>
      </div>
      <div v-else>
        <div class="center grey-text amount-label" v-if="showCashLabel">
          Cash/Check price:
        </div>
        <h3 class="bold-font center">Payment {{amount | currency}}</h3>
      </div>

      <div class='large-font mt-5 center'>
        to: {{businessName}}
      </div>

      <div class="row">
        <div class="col s12 m6" v-if="invoiceFieldEnabled">
          <div v-if="presetInvoice.length > 0" class="large-font center mt-25">
            <i class="fas fa-file-invoice prefix grey-text"></i>
            Invoice: <span class="bold-font">{{invoice}}</span>
          </div>
          <div v-else class="input-field">
            <i class="fas fa-file-invoice prefix grey-text"></i>
            <input name="invoice" id="invoice" type="text" v-model="invoice" ref="invoice">

            <label for="invoice">Invoice</label>
          </div>
        </div>

        <div class="input-field col s12 m6" v-if="noteFieldEnabled">
          <i class="fas fa-sticky-note prefix grey-text"></i>
          <input name="note" id="note" type="text" v-model="note" ref="note">

          <label for="note">Note (Optional)</label>
        </div>
      </div>

      <div class="row" v-if="nameFieldEnabled">
        <div class="input-field col s12 m6">
          <i class="fas fa-user prefix grey-text"></i>
          <input name="first_name" id="first_name" type="text" autocomplete="given-name" v-model="firstName" ref="first_name" required>

          <label for="first_name">Customer's First Name</label>
        </div>

        <div class="input-field col s12 m6">
          <input name="last_name" id="last_name" type="text" autocomplete="family-name" v-model="lastName" ref="last_name" required>

          <label for="last_name">Customer's Last Name</label>
        </div>
      </div>

      <div v-if="addressFieldEnabled">
        <div class="row">
          <div class="input-field col s12 m6">
            <i class="fas fa-map-marker-alt prefix grey-text"></i>
            <input name="address1" id="address1" type="text" autocomplete="address-line1" v-model="address.address1" maxlength="50" required :class="{invalid: addressFieldInvalid('address1')}">

            <label for="address1">Address</label>
          </div>

          <div class="input-field col s12 m6">
            <input name="address2" id="address2" type="text" autocomplete="address-line2" v-model="address.address2" maxlength="50">

            <label for="address2">Apt, suite, etc. (Optional)</label>
          </div>
        </div>

        <div class="row">
          <div class="input-field col s12 m6 prefix-offset-m">
            <input name="city" id="city" type="text" autocomplete="address-level2" v-model="address.city" maxlength="50" required :class="{invalid: addressFieldInvalid('city')}">

            <label for="city">City</label>
          </div>

          <div class="input-field col s6 m3">
            <state-select v-model="address.state" placeholder="Select state" native name="state" id="state" autocomplete="address-level1" required :class="{invalid: addressFieldInvalid('state')}"></state-select>

            <label for="state" class="active">State</label>
          </div>

          <div class="input-field col s6 m3">
            <input name="postal_code" id="postal_code" type="text" autocomplete="postal-code" v-model="address.postal_code" maxlength="10" inputmode="numeric" required :class="{invalid: addressFieldInvalid('postal_code')}">

            <label for="postal_code">ZIP Code</label>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="input-field col s12 m6" v-if="emailFieldEnabled">
          <i class="fas fa-envelope prefix grey-text"></i>
          <input name="email" id="email" type="text" autocomplete="email" inputmode="email" v-model="email" ref="email" required>

          <label for="email">Customer's Email</label>
        </div>

        <div class="input-field col s12 m6" v-if="phoneFieldEnabled">
          <i class="fas fa-phone prefix grey-text"></i>
          <input name="phone" id="phone" type="text" autocomplete="tel" inputmode="tel" v-model="phone" ref="phone">

          <label for="phone">Customer's Mobile Phone</label>
        </div>
      </div>

      <section class='mt-10 grey-text center' v-if='showTips'>
        <tips :qid="merchantQid" :tips-updated="tipsUpdated"></tips>
      </section>

      <div class="mt-10">
        <slot :update-token="updateToken"></slot>
      </div>

      <div class="mt-10" v-if="errorMessage">
        <span class='red-text'>{{errorMessage}}</span>
      </div>

      <div class="center mt-5" v-if="useCaptcha">
        <div v-if="captchaLoading" class="grey-text">
          <i class="fas fa-spinner fa-spin"></i> Loading captcha...
        </div>
        <turnstile
          v-show="!captchaLoading"
          :site-key="turnstileSiteKey"
          @rendered="onTurnstileRendered"
          @verified="onTurnstileVerified"
          @error="onTurnstileError"
          @expired="onTurnstileExpired"
          theme="auto"
          size="normal">
        </turnstile>
      </div>

      <div class="center mt-5" v-show="requirePolicyConsent">
        <label>
          <input type="checkbox" v-model="agreedToTerms" :disabled="showPending"/>
          <span>I agree to the <a :href="policyUrl" target="_blank">company policy</a>
          and to pay the above amount</span>
        </label>
      </div>
      <button @click="charge" v-show="!showPending" class="btn-large wide-btn mt-5" :disabled="!isPaymentEnabled">
        pay <span v-show='amount'>{{withTipAmount}}</span>
      </button>

      <div class="center emerald mt-5 large-font bold-font" v-if="showSurchargeLabel && feeAmount > 0">
        {{surchargeLabel}}: <b>{{grossAmount | currency}}</b>
      </div>
      <div class="center emerald mt-5 large-font bold-font" v-else-if="feeAmount > 0">
        Gross amount: <b>{{grossAmount | currency}}</b> |
        Non-cash adj: <b>{{feeAmount | currency}}</b>
      </div>

      <center v-if='showPending' class='grey-text mt-25'>
        <i class="fas fa-spinner fa-spin"></i> Processing...
      </center>
    </section>
    <section v-show="showSuccess" class="mt-50 center">
      <h3 class='emerald bold-font'>
        <i class="fas fa-check fa-3x"></i><br>
        Successfully Paid
      </h3>
    </section>
  </div>
</template>
<script>
import tips from "../tips"
import Turnstile from "../turnstile"
import stateSelect from "components/utils/states_select"

// Required address inputs, in on-screen order; keys double as the input ids
const ADDRESS_FIELDS = [
  {key: 'address1', label: 'address'},
  {key: 'city', label: 'city'},
  {key: 'state', label: 'state'},
  {key: 'postal_code', label: 'ZIP code'}
]
const ZIP_FORMAT = /^\d{5}(-\d{4})?$/

const toSentence = words => words.length > 1 ?
  `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}` :
  words[0]

export default {
  components: {tips, Turnstile, 'state-select': stateSelect},
  props: [
    'paymentType',
    'presetAmount',
    'presetInvoice',
    'presetEmail',
    'presetPhone',
    'presetAddressZip',
    'presetFirstName',
    'presetLastName',
    'presetNote',
    'amountEditable',
    'invoiceEditable',
    'noteEditable',
    'businessName',
    'merchantQid',
    'showTips',
    'checkoutSessionId',
    'custom',
    'invoiceFieldEnabled',
    'noteFieldEnabled',
    'phoneFieldEnabled',
    'emailFieldEnabled',
    'addressFieldEnabled',
    'nameFieldEnabled',
    'requirePolicyConsent',
    'policyUrl',
    'useCaptcha',
    'turnstileSiteKey'
  ],
  created() {
    this.$dom.watch() // to render icons
    this.updateAmounts()
    this.$nextTick(() => {
      M.updateTextFields();
    });
  },
  data() {
    return {
      amount: this.presetAmount,
      invoice: this.presetInvoice === '' ? null : this.presetInvoice,
      note: this.presetNote === '' ? null : this.presetNote,
      email: this.presetEmail === '' ? null : this.presetEmail,
      phone: this.presetPhone === '' ? null : this.presetPhone,
      firstName: this.presetFirstName === '' ? null : this.presetFirstName,
      lastName: this.presetLastName === '' ? null : this.presetLastName,
      address: {
        address1: null,
        address2: null,
        city: null,
        state: null,
        postal_code: this.presetAddressZip || null
      },
      selectedTip: null,
      grossAmount: null,
      feeAmount: 0,
      withTipAmount: null,
      tipAmount: null,
      tipAmountFormatted: null,
      showSurchargeLabel: false,
      surchargeLabel: '',
      cardToken: {},
      turnstileToken: null,
      captchaLoading: this.useCaptcha,
      showPending: false,
      showSuccess: false,
      lastGrossCalcRequestId: 0,
      agreedToTerms: false,
      addressChecked: false,
      errorMessage: null
    }
  },
  methods: {
    tipsUpdated(selectedTip, tipCustomAmount) {
      this.selectedTip = selectedTip
      this.tipCustomAmount = tipCustomAmount
      this.updateAmounts()
    },
    updateAmounts() {
      this.lastGrossCalcRequestId++;
      const currentRequestId = this.lastGrossCalcRequestId;

      this.$http.get("/api/v1/vue/gross_calculations", {params: this.grossCalcParams}).then(response => {
        if (currentRequestId === this.lastGrossCalcRequestId) {
          this.grossAmount = response.body.gross_amount
          this.feeAmount = response.body.fee
          this.withTipAmount = response.body.amount_with_tip
          this.tipAmount = response.body.tip_amount
          this.tipAmountFormatted = response.body.tip_amount_formatted
          this.showSurchargeLabel = response.body.show_surcharge_label
          this.surchargeLabel = response.body.surcharge_label
        }
      })
    },
    charge() {
      if (this.requirePolicyConsent && !this.agreedToTerms) {
        this.notifyError("Please agree to the company policy before proceeding with payment")
        return
      }

      if (this.addressFieldEnabled && !this.checkAddress()) {
        return
      }

      this.errorMessage = null
      this.showPending = true
      this.$http.post("/api/v1/public/checkout_sessions/card_payments", this.chargeParams ).then(response => {
          this.showSuccess = true
          this.notifySuccess("Transaction successfully processed")
        }, error => {
          this.showPending = false
          this.cardToken = {}

          // Extract error message for display
          if (error.body && error.body.error_messages && error.body.error_messages.length > 0) {
            this.errorMessage = error.body.error_messages[0]
          } else if (error.body && error.body.error) {
            this.errorMessage = error.body.error
          } else {
            this.errorMessage = 'Something went wrong'
          }

          // Trigger card elements to regenerate token from existing card data
          this.$bus.$emit('regenerate-card-token')

          this.errorHandler(error)
        })
    },
    // Mirrors the server's address rules so the payer sees every missing field
    // at once; each server rejection costs a round trip and a fresh card token.
    checkAddress() {
      this.addressChecked = true
      const missing = ADDRESS_FIELDS.filter(field => this.addressFieldInvalid(field.key))
      if (missing.length === 0) {
        return true
      }

      const blank = missing.filter(field => !this.addressValue(field.key))
      this.errorMessage = blank.length > 0 ?
        `Please enter your ${toSentence(blank.map(field => field.label))}` :
        "ZIP code must be 12345 or 12345-1234"
      this.notifyError(this.errorMessage)
      this.$nextTick(() => document.getElementById(missing[0].key).focus())
      return false
    },
    addressValue(key) {
      return (this.address[key] || '').trim()
    },
    addressFieldInvalid(key) {
      if (!this.addressChecked) {
        return false
      }

      const value = this.addressValue(key)
      return key === 'postal_code' ? !ZIP_FORMAT.test(value) : !value
    },
    updateToken(token) {
      this.cardToken = token
    },
    onTurnstileRendered() {
      this.captchaLoading = false
    },
    onTurnstileVerified(token) {
      this.turnstileToken = token
      this.captchaLoading = false
    },
    onTurnstileError(error) {
      console.error('Turnstile error:', error)
      this.turnstileToken = null
      this.captchaLoading = false
      if (error === 'Turnstile script not available' || error === 'Turnstile not available') {
        this.notifyError("Captcha failed to load. Please refresh the page.")
      } else {
        this.notifyError("Captcha verification failed. Please try again.")
      }
    },
    onTurnstileExpired() {
      console.warn('Turnstile token expired')
      this.turnstileToken = null
      this.captchaLoading = false
      this.notifyError("Captcha expired. Please solve it again.")
    }
  },
  computed: {
    // with a tip the displayed amount no longer is the cash price, so the label would lie
    showCashLabel() {
      return this.showSurchargeLabel && this.feeAmount > 0 && !this.tipAmount
    },
    grossCalcParams() {
      return {
        amount: this.amount,
        tip_percent: this.selectedTip,
        tip_custom_amount: this.tipCustomAmount,
        qid: this.merchantQid,
        funding: this.cardToken.funding
      }
    },
    chargeParams() {
      return {
        amount: this.amount,
        token: this.cardToken.token,
        note: this.note,
        first_name: this.firstName,
        last_name: this.lastName,
        address: this.addressFieldEnabled ? this.address : null,
        email: this.email,
        phone: this.phone,
        invoice: this.invoice,
        tip_percent: this.selectedTip,
        tip_amount: this.tipCustomAmount,
        checkout_session_id: this.checkoutSessionId,
        custom: this.custom,
        turnstile_token: this.turnstileToken
      }
    },
    isPaymentEnabled() {
      const hasCardToken = !!this.cardToken.token
      const hasCaptcha = !this.useCaptcha || !!this.turnstileToken
      return hasCardToken && hasCaptcha
    }
  },
  watch: {
    "cardToken.token": function(newVal, oldVal) {
      this.updateAmounts();
    }
  }
}
</script>
<style>
  /* keeps the cash/check caption glued to the amount it describes */
  .amount-label + h3 {
    margin-top: 0;
  }
</style>
